/**
 * 単位系・モデル定数の単一定義モジュール（設計書 §4.9）。
 * 換算定数をここ以外で再定義することを禁止する。
 * 「校正: 要」の値はすべて初期値であり、§15の校正工程で確定させる。
 */

/** UVI 1 = 紅斑加重照度 0.025 W/m²（WHO定義） */
export const UVI_TO_WM2 = 0.025;

/** 1 SED = 100 J/m²（定義） */
export const JOULES_PER_SED = 100;

/** UVI 1 を1時間浴び続けたときの線量 [SED]（0.025×3600÷100 = 0.9） */
export const SED_PER_UVI_HOUR = (UVI_TO_WM2 * 3600) / JOULES_PER_SED;

/** 積分tick間隔 [s]（§4.2 1分tick） */
export const TICK_SECONDS = 60;

/** 姿勢・部位係数（§4.6 全身平均の固定値）【初期値・要校正】 */
export const F_POSE = 0.65;

/** 散乱比写像 fd_uv = clip(A + B×fd_bb, MIN, MAX)（§4.3）【初期値・要校正】 */
export const FD_UV_OFFSET = 0.45;
export const FD_UV_SLOPE = 0.5;
export const FD_UV_MIN = 0.45;
export const FD_UV_MAX = 0.95;

/** 日焼け止め減衰時定数 [s]（§4.5）【初期値・要校正】 */
export const TAU_NORMAL_S = 6 * 3600;
export const TAU_HOT_HUMID_S = 3 * 3600;
/** τ環境補正の発動条件（気温≥30℃ かつ 湿度≥70%） */
export const HOT_TEMP_C = 30;
export const HUMID_RH = 70;

/** 水泳残存係数（§4.5）【初期値・要校正】 */
export const SWIM_KEEP_WATERPROOF = 0.7;
export const SWIM_KEEP_NORMAL = 0.3;

/** 塗布量比 r の3択（§4.5 たっぷり／ふつう／うすく） */
export const APPLY_AMOUNT_R = { full: 1.0, normal: 0.6, thin: 0.3 } as const;
export type ApplyAmount = keyof typeof APPLY_AMOUNT_R;

/** 警告閾値係数（§4.8）S_warn = S_WARN_RATIO × MED【初期値】 */
export const S_WARN_RATIO = 0.7;

/**
 * MED初期値 [SED]（Fitzpatrickスキンタイプ別、§4.8）【初期値・実装時に文献で確定】
 * 日本人はII〜IVが大半。
 */
export const MED_SED: Record<1 | 2 | 3 | 4, number> = {
  1: 2.0,
  2: 2.5,
  3: 3.0,
  4: 4.5,
};

/** 屋内判定：GPS精度がこの値[m]を超える状態がこの時間[s]継続で屋内とみなす（§7.3） */
export const INDOOR_ACC_M = 35;
export const INDOOR_ACC_DURATION_S = 3 * 60;
/** 屋外らしい欠測に日向仮定を適用する上限 [s]（§7.3 安全側） */
export const GAP_SUN_ASSUME_MAX_S = 10 * 60;

/** 外出モードのセッション上限 [s]（§7.2 バッテリー保護） */
export const SESSION_MAX_S = 8 * 3600;

/** アラート抑制（§9.2） */
export const ALERT_COOLDOWN_S = 20 * 60;
export const ALERT_MAX_PER_DAY = 8;
export const QUIET_HOUR_START = 22; // JST
export const QUIET_HOUR_END = 6;

/** アラートトリガ閾値（§9.1） */
export const T15_REMAIN_MIN = 15;
export const SUN_IN_SUSTAIN_S = 3 * 60;
export const CMF_JUMP_DELTA = 0.3;
export const CMF_JUMP_WINDOW_S = 10 * 60;
export const DECAY_THRESHOLD = 0.5;

/** 気象セル格子幅 [deg]（§5.5 約5km格子） */
export const WX_CELL_DEG = 0.05;

/** 水平線角マップの方位セクタ数（§5.1） */
export const HORIZON_SECTORS = 16;
/** 水平線角の量子化刻み [deg]（§5.2 uint8） */
export const HORIZON_QUANT_DEG = 0.5;
/** 地形セル解像度 [m]（§5.2） */
export const TERRAIN_CELL_M = 5;
/** 地形タイル1辺のセル数（§5.2） */
export const TERRAIN_TILE_CELLS = 256;

/** 観測高 [m]（§5.3） */
export const OBSERVER_HEIGHTS = { adult: 1.5, stroller: 0.5 } as const;
export type HeightMode = keyof typeof OBSERVER_HEIGHTS;

/** 直射換算の基準レート [SED/h]（§10.1 UVI8相当の真夏快晴正午） */
export const DIRECT_SUN_SED_PER_H = 8 * SED_PER_UVI_HOUR; // 7.2

/**
 * 感覚的な日焼け量の単位「P」（§10.1の直射換算を生活実感に置き換えたもの）。
 * 1P = 真夏の晴天（UVI8相当）のプールに3時間、日焼け止めなしでいた場合の被曝量。
 *
 * ルート・日次の被曝と同じモデル（開けた場所・全身平均F_pose・反射は非考慮）で
 * 算出するため、比較が同一モデル上で閉じる。実際のプールサイドは水面・コンクリート
 * からの反射があり、反射項が入る第2段では基準値が上振れする（§13リスク3）。
 * F_POSEの校正（§15.3）に追随して値が変わる【初期値】。
 */
export const POOL_REFERENCE_HOURS = 3;

/** 1P あたりのSED（= 7.2 SED/h × 3h × F_pose ≒ 14.0 SED） */
export const SED_PER_POOL = DIRECT_SUN_SED_PER_H * POOL_REFERENCE_HOURS * F_POSE;

/** 歩行速度 [m/min]（経路探索の所要時間換算） */
export const WALK_SPEED_M_PER_MIN = 80;

/** 日陰ルートの時間上限の既定値（設定画面で変更可能） */
export const DEFAULT_ROUTE_PREFS = {
  maxTimeRatio: 1.5,
  maxExtraMinutes: 10,
} as const;

/** 自動外出モード：自宅からこの距離[m]以上離れてアプリを開くと計測を自動開始 */
export const AUTO_START_DISTANCE_M = 100;
